import { type FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PencilSquareIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useProductDetail } from '../../hooks/useProductDetail';
import { useProductEnrichment } from '../../hooks/useProductEnrichment';
import ProductForm from './components/ProductForm';

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatPrice = (price: number) =>
    new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(price / 100);

const formatDateTime = (value: string | null) =>
    value ? new Date(value).toLocaleString() : '—';

// Nutritional info is stored as free text and occasionally contains stray
// literal "<br>" fragments left over from the source export's formatting —
// not real HTML, just noise. Swap those for real newlines. Used as a raw
// fallback if parseNutritionalInfo can't make sense of a given entry.
const formatNutritionalInfo = (value: string) => value.replace(/<br\s*\/?>/gi, '\n');

const DIETARY_FLAGS: { key: 'is_vegan' | 'is_vegetarian' | 'is_organic' | 'is_halaal' | 'is_kosher'; label: string }[] = [
    { key: 'is_vegan', label: 'Vegan' },
    { key: 'is_vegetarian', label: 'Vegetarian' },
    { key: 'is_organic', label: 'Organic' },
    { key: 'is_halaal', label: 'Halaal' },
    { key: 'is_kosher', label: 'Kosher' },
];

// ── Nutrition parsing ────────────────────────────────────────────────────────
// Source text looks like:
//   "Per 100g/ml\nEnergy: 357KJ\nCarbohydrates: 4g\n  • Of which total sugar: 1.9g\n...
//    <br>Per Serving (125g)\nEnergy: 447KJ\n..."
// It's free text from the supplier, not structured data, but it's consistent
// enough to parse into real nutrition-label rows in the common case. If a
// given entry doesn't match this shape at all, fall back to the raw text
// rather than showing something broken or empty.

interface NutritionRow {
    label: string;
    value: string;
    indent: boolean;
}

interface NutritionSection {
    title: string;
    rows: NutritionRow[];
}

const parseNutritionalInfo = (raw: string): NutritionSection[] => {
    const lines = formatNutritionalInfo(raw)
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean);

    const sections: NutritionSection[] = [];
    let current: NutritionSection | null = null;

    for (const line of lines) {
        if (/^per\s/i.test(line)) {
            current = { title: line, rows: [] };
            sections.push(current);
            continue;
        }

        const indent = line.startsWith('•');
        const cleaned = line.replace(/^•\s*/, '');
        const separatorIndex = cleaned.indexOf(':');
        if (separatorIndex === -1) continue; // not a "Label: value" line — skip rather than guess

        if (!current) {
            current = { title: 'Nutritional Information', rows: [] };
            sections.push(current);
        }
        current.rows.push({
            label: cleaned.slice(0, separatorIndex).trim(),
            value: cleaned.slice(separatorIndex + 1).trim(),
            indent,
        });
    }

    return sections;
};

// ── Nutrition Facts panel ────────────────────────────────────────────────────
// Deliberately styled after an actual food nutrition label — bold top/bottom
// rules, tabular figures — since that's a convention people already know how
// to read at a glance, rather than another generic bordered card.

const NutritionFactsPanel: FC<{ raw: string }> = ({ raw }) => {
    const sections = parseNutritionalInfo(raw);
    const [activeIndex, setActiveIndex] = useState(0);

    if (sections.length === 0) {
        return (
            <div className="rounded-2xl border-2 border-stone-300 overflow-hidden bg-white">
                <div className="px-4 py-3 border-b-4 border-stone-300">
                    <p className="text-base font-extrabold tracking-tight text-stone-900">Nutrition Facts</p>
                </div>
                <pre className="whitespace-pre-wrap font-sans text-sm text-stone-600 leading-6 p-4">
            {formatNutritionalInfo(raw)}
          </pre>
            </div>
        );
    }

    const active = sections[Math.min(activeIndex, sections.length - 1)];

    return (
        <div className="rounded-2xl border-2 border-stone-300 overflow-hidden bg-white">
            <div className="px-4 py-3 border-b-2 border-stone-300 flex flex-wrap items-center justify-between gap-3">
                <p className="text-base font-extrabold tracking-tight text-stone-900">Nutrition Facts</p>
                {sections.length > 1 && (
                    <div className="flex rounded-full bg-stone-100 p-0.5">
                        {sections.map((section, i) => (
                            <button
                                key={section.title}
                                type="button"
                                onClick={() => setActiveIndex(i)}
                                className={`px-3 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                                    i === activeIndex
                                        ? 'bg-[#ee2b74] text-white'
                                        : 'text-stone-500 hover:text-stone-700'
                                }`}
                            >
                                {section.title}
                            </button>
                        ))}
                    </div>
                )}
            </div>
            <div className="divide-y divide-stone-100">
                {active.rows.map((row, i) => (
                    <div
                        key={`${row.label}-${i}`}
                        className={`flex items-baseline justify-between gap-4 px-4 py-2 ${
                            row.indent ? 'pl-8 text-stone-500' : 'text-stone-800 font-semibold'
                        }`}
                    >
                        <span className="text-sm">{row.label}</span>
                        <span className="font-mono text-sm tabular-nums whitespace-nowrap">{row.value}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

// ── Component ─────────────────────────────────────────────────────────────────

const PriceHistoryPage: FC = () => {
    const { productId, storeId } = useParams<{ productId: string; storeId: string }>();
    const { product, prices, loading, fetchProductDetail } = useProductDetail();
    const { enrichment, loading: enrichmentLoading, fetchEnrichment } = useProductEnrichment();
    const [showEditForm, setShowEditForm] = useState(false);

    useEffect(() => {
        if (productId) void fetchProductDetail(productId, storeId);
    }, [productId, storeId, fetchProductDetail]);

    useEffect(() => {
        if (productId) void fetchEnrichment(productId);
    }, [productId, fetchEnrichment]);

    const currentPrice = product?.display_price ?? prices[0]?.price ?? 0;
    const displayDescription = enrichment?.description ?? product?.description ?? null;
    const matchedDietaryFlags = enrichment
        ? DIETARY_FLAGS.filter(({ key }) => enrichment[key] === true)
        : [];
    const packSummary = enrichment
        ? [enrichment.pack_configuration, [enrichment.unit_of_size, enrichment.unit_of_measure].filter(Boolean).join(' ')]
            .filter(Boolean)
            .join(' · ')
        : '';

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-2xl font-semibold text-stone-900">Product Details</h2>
                <p className="mt-1 text-sm text-stone-500">
                    View product information, status, pricing, and price changes in one place.
                </p>
            </div>

            {/* Product card */}
            <div className="rounded-2xl border border-stone-200 bg-white shadow-sm overflow-hidden">
                <div className="h-1 bg-[#ee2b74]" />
                {loading ? (
                    <div className="p-6 animate-pulse">
                        <div className="h-6 w-1/3 rounded bg-stone-100 mb-3" />
                        <div className="h-4 w-1/2 rounded bg-stone-100 mb-6" />
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {Array.from({ length: 4 }).map((_, i) => (
                                <div key={i} className="h-20 rounded-xl bg-stone-100" />
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="p-6">
                        <div className="flex flex-col lg:flex-row lg:items-start gap-5">
                            <div className="w-full lg:w-32 h-32 rounded-2xl border border-stone-200 bg-stone-50 overflow-hidden flex items-center justify-center flex-shrink-0">
                                {product?.image_url ? (
                                    <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                                ) : (
                                    <span className="text-xs font-medium text-stone-400">No image</span>
                                )}
                            </div>

                            <div className="flex-1">
                                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                                    <div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <h3 className="text-xl font-semibold text-stone-900">
                                                {product?.name ?? 'Unknown Product'}
                                            </h3>
                                            <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${
                                                product?.is_active
                                                    ? 'bg-[#f0f7e6] text-[#5a7f28] ring-[#c5e08a]'
                                                    : 'bg-stone-100 text-stone-600 ring-stone-200'
                                            }`}>
                                                {product?.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                            <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${
                                                product?.health
                                                    ? 'bg-blue-50 text-blue-700 ring-blue-200'
                                                    : 'bg-stone-100 text-stone-600 ring-stone-200'
                                            }`}>
                                                {product?.health ? 'Health Product' : 'Standard Product'}
                                            </span>
                                        </div>

                                        <div className="mt-2 flex flex-wrap gap-2">
                                            <span className="inline-flex rounded-md bg-stone-100 px-2 py-1 font-mono text-xs text-stone-600">
                                                SKU: {product?.sku ?? '—'}
                                            </span>
                                            <span className="inline-flex rounded-md bg-stone-100 px-2 py-1 text-xs text-stone-600">
                                                Store: {product?.store_name ?? 'No Store'}
                                            </span>
                                            <span className="inline-flex rounded-md bg-stone-100 px-2 py-1 text-xs text-stone-600">
                                                Stock: {product?.stock ?? 0}
                                            </span>
                                        </div>

                                        {product?.keywords && product.keywords.length > 0 && (
                                            <div className="mt-4">
                                                <p className="text-[10px] font-bold uppercase tracking-wider text-stone-400 mb-2">Keywords</p>
                                                <div className="flex flex-wrap gap-2">
                                                    {product.keywords.map(kw => (
                                                        <span key={kw} className="inline-flex items-center rounded-full bg-stone-100 px-3 py-1 text-xs font-medium text-stone-700">
                                                            {kw}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {displayDescription && (
                                            <div className="mt-4">
                                                <p className="text-[10px] font-bold uppercase tracking-wider text-stone-400 mb-2">Description</p>
                                                <p className="max-w-3xl text-sm text-stone-600 leading-6">{displayDescription}</p>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex flex-col gap-3">
                                        <div className="rounded-2xl bg-stone-50 border border-stone-200 px-4 py-3 min-w-[180px]">
                                            <p className="text-[10px] font-bold uppercase tracking-wider text-stone-400">Current Price</p>
                                            <p className="mt-1 text-2xl font-semibold text-stone-900">{formatPrice(currentPrice)}</p>
                                            <p className="mt-1 text-xs text-stone-500">Latest available price</p>
                                        </div>
                                        <button type="button" onClick={() => setShowEditForm(true)}
                                                className="inline-flex items-center justify-center gap-2 rounded-xl border border-stone-200 bg-white px-4 py-2.5 text-sm font-medium text-stone-700 shadow-sm hover:bg-stone-50 transition-colors">
                                            <PencilSquareIcon className="h-4 w-4" />
                                            Edit Product
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Product enrichment */}
            {enrichmentLoading ? (
                <div className="rounded-2xl border border-stone-200 bg-white shadow-sm overflow-hidden">
                    <div className="p-6 animate-pulse">
                        <div className="h-4 w-1/4 rounded bg-stone-100 mb-4" />
                        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                            <div className="lg:col-span-3 space-y-3">
                                <div className="h-4 w-2/3 rounded bg-stone-100" />
                                <div className="h-4 w-1/2 rounded bg-stone-100" />
                                <div className="h-4 w-3/4 rounded bg-stone-100" />
                            </div>
                            <div className="lg:col-span-2 h-48 rounded-xl bg-stone-100" />
                        </div>
                    </div>
                </div>
            ) : enrichment ? (
                <div className="rounded-2xl border border-stone-200 bg-white shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b border-stone-200 bg-stone-50/60 flex flex-wrap items-start justify-between gap-4">
                        <div>
                            <h4 className="text-sm font-semibold text-stone-900">Product Enrichment</h4>
                            <p className="text-xs text-stone-500 mt-1">
                                Supplier-provided details, nutritional information, and dietary certifications.
                            </p>
                        </div>
                        {matchedDietaryFlags.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                                {matchedDietaryFlags.map(({ key, label }) => (
                                    <span key={key} className="inline-flex items-center gap-1 rounded-full bg-[#f0f7e6] px-2.5 py-1 text-xs font-medium text-[#5a7f28] ring-1 ring-[#c5e08a]">
                            <CheckCircleIcon className="h-3.5 w-3.5" />
                                        {label}
                          </span>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="p-6 space-y-6">
                        {(enrichment.brand || packSummary) && (
                            <div className="flex flex-wrap items-center gap-2">
                                {enrichment.brand && (
                                    <span className="inline-flex items-center rounded-full bg-stone-100 px-3 py-1 text-xs font-semibold text-stone-700">
                            {enrichment.brand}{enrichment.sub_brand ? ` — ${enrichment.sub_brand}` : ''}
                          </span>
                                )}
                                {packSummary && (
                                    <span className="text-xs text-stone-500">{packSummary}</span>
                                )}
                            </div>
                        )}

                        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                            <div className="lg:col-span-3 space-y-5">
                                {enrichment.features_and_benefits && (
                                    <div className="rounded-xl bg-stone-50 border border-stone-200 p-4">
                                        <p className="text-[10px] font-bold uppercase tracking-wider text-stone-400 mb-2">Features &amp; Benefits</p>
                                        <p className="text-sm text-stone-600 leading-6">{enrichment.features_and_benefits}</p>
                                    </div>
                                )}

                                {enrichment.ingredients && (
                                    <div>
                                        <p className="text-[10px] font-bold uppercase tracking-wider text-stone-400 mb-2">Ingredients</p>
                                        <p className="text-sm text-stone-600 leading-6">{enrichment.ingredients}</p>
                                    </div>
                                )}

                                {(enrichment.common_allergens && enrichment.common_allergens.length > 0) || enrichment.allergen_statement ? (
                                    <div>
                                        <p className="text-[10px] font-bold uppercase tracking-wider text-stone-400 mb-2">Allergens</p>
                                        {enrichment.common_allergens && enrichment.common_allergens.length > 0 && (
                                            <div className="flex flex-wrap gap-2 mb-2">
                                                {enrichment.common_allergens.map(allergen => (
                                                    <span key={allergen} className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800 ring-1 ring-amber-200">
                                      {allergen}
                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        {enrichment.allergen_statement && (
                                            <p className="text-sm text-stone-600 leading-6">{enrichment.allergen_statement}</p>
                                        )}
                                    </div>
                                ) : null}
                            </div>

                            {enrichment.nutritional_info_raw && (
                                <div className="lg:col-span-2">
                                    <NutritionFactsPanel raw={enrichment.nutritional_info_raw} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            ) : null}

            {/* Price history */}
            <div className="rounded-2xl border border-stone-200 bg-white shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-stone-200 bg-stone-50/60">
                    <h4 className="text-sm font-semibold text-stone-900">Price Changes</h4>
                    <p className="text-xs text-stone-500 mt-1">Historical pricing entries for this product.</p>
                </div>
                <div className="overflow-auto">
                    <table className="min-w-full text-sm">
                        <thead className="bg-white border-b border-stone-200">
                        <tr>
                            {['Price', 'Valid From', 'Valid To', 'Status'].map(h => (
                                <th key={h} className="py-3 px-6 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">{h}</th>
                            ))}
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100">
                        {loading ? (
                            <tr><td colSpan={4} className="py-10 px-6 text-stone-400">Loading…</td></tr>
                        ) : prices.length === 0 ? (
                            <tr><td colSpan={4} className="py-10 px-6 text-stone-400">No history found</td></tr>
                        ) : prices.map((r, i) => {
                            const isCurrent = !r.valid_to;
                            return (
                                <tr key={i} className="hover:bg-stone-50/50 transition-colors">
                                    <td className="py-4 px-6 font-semibold text-stone-900">{formatPrice(r.price)}</td>
                                    <td className="py-4 px-6 text-stone-600">{formatDateTime(r.valid_from)}</td>
                                    <td className="py-4 px-6 text-stone-600">{formatDateTime(r.valid_to)}</td>
                                    <td className="py-4 px-6">
                                            <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${
                                                isCurrent
                                                    ? 'bg-[#f0f7e6] text-[#5a7f28] ring-[#c5e08a]'
                                                    : 'bg-stone-100 text-stone-600 ring-stone-200'
                                            }`}>
                                                {isCurrent ? 'Current' : 'Ended'}
                                            </span>
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Edit modal */}
            {showEditForm && product && (
                <ProductForm
                    initial={{
                        id:            product.id,
                        sku:           product.sku,
                        name:          product.name,
                        store_id:      product.store_id ?? null,
                        current_price: product.display_price ?? 0,
                        stock:         product.stock ?? 0,
                        description:   product.description ?? '',
                        specification: product.specification ?? '',
                        keywords:      product.keywords ?? [],
                        is_active:     product.is_active ?? true,
                    }}
                    onClose={() => setShowEditForm(false)}
                    onSaved={() => {
                        setShowEditForm(false);
                        if (productId) void fetchProductDetail(productId, storeId);
                    }}
                />
            )}
        </div>
    );
};

export default PriceHistoryPage;